import type {
  BusinessModeCode,
  BusinessModeRecord,
  LegacyBusinessModeCode,
  StableBusinessModeCode,
} from './types';

const DEFAULT_MODES: readonly BusinessModeRecord[] = [
  { code: 'rental', label: '租赁', sortOrder: 10, active: true },
  {
    code: 'short_rental',
    label: '免押租赁（短租租金）',
    sortOrder: 15,
    active: true,
  },
  { code: 'full', label: '全案', sortOrder: 20, active: true },
  { code: 'buyout', label: '买断', sortOrder: 30, active: true },
  { code: 'repair', label: '维修调试', sortOrder: 40, active: true },
];

const LEGACY_LABELS: Record<LegacyBusinessModeCode, string> = {
  trade: '商贸',
  service: '服务',
};

export const DEFAULT_QUOTE_BUSINESS_MODE: StableBusinessModeCode = 'rental';

export function isRentalBusinessMode(
  code: BusinessModeCode,
): code is 'rental' | 'short_rental' {
  return code === 'rental' || code === 'short_rental';
}

export function writableQuoteBusinessModeCode(
  code: BusinessModeCode,
): 'full' | 'rental' | 'short_rental' | 'buyout' {
  return code === 'buyout' ? code : isRentalBusinessMode(code) ? code : 'full';
}

/** Approved workbench defaults. `full` is the persisted representation of the
 * blank/unselected workbench choice and currently follows rental pricing. */
export function defaultQuoteDiscountForBusinessMode(
  code: BusinessModeCode,
): number {
  return isRentalBusinessMode(code) || code === 'full' ? 88 : 100;
}

const QUOTE_WORKBENCH_MODE_LABELS: Readonly<
  Record<'rental' | 'short_rental' | 'buyout', string>
> = {
  rental: '免押租赁（年签月付）',
  short_rental: '免押租赁（短租租金）',
  buyout: '设备购买',
};

const QUOTE_WORKBENCH_MODE_CODES = [
  'rental',
  'short_rental',
  'buyout',
] as const;

export const LEGACY_BUSINESS_MODE_SETTING_KEY = '业务模式';
/** Removed because these Disposable Mock settings had no runtime consumer. */
export const REMOVED_SYSTEM_SETTING_KEYS = [
  LEGACY_BUSINESS_MODE_SETTING_KEY,
  'UI 样式配置',
  '店铺名称映射',
  '系统公告',
] as const;

export function createBusinessModeCatalog(): BusinessModeRecord[] {
  return DEFAULT_MODES.map((mode) => ({ ...mode }));
}

export function activeBusinessModes(
  modes: BusinessModeRecord[],
): BusinessModeRecord[] {
  return modes
    .filter((mode) => mode.active)
    .sort((left, right) => left.sortOrder - right.sortOrder);
}

/** The quote workbench only presents the two approved business choices.
 * Historical `full` and repair records remain readable but cannot be newly
 * selected from the workbench. */
export function quoteWorkbenchBusinessModes(
  modes: BusinessModeRecord[],
): Array<{ code: 'rental' | 'short_rental' | 'buyout'; label: string }> {
  return QUOTE_WORKBENCH_MODE_CODES.flatMap((code) => {
    const mode = modes.find((candidate) => candidate.code === code);
    if (!mode?.active) return [];
    return [
      {
        code,
        label: QUOTE_WORKBENCH_MODE_LABELS[code],
      },
    ];
  });
}

/** Match the label shown in the workbench selector, including its approved aliases. */
export function quoteWorkbenchBusinessModeDisplayName(
  modes: BusinessModeRecord[],
  code: BusinessModeCode,
  legacySnapshot: string,
): string {
  return (
    quoteWorkbenchBusinessModes(modes).find((mode) => mode.code === code)
      ?.label ?? currentBusinessModeLabel(modes, code, legacySnapshot)
  );
}

export function quoteWorkbenchBusinessModeValue(
  code: BusinessModeCode,
): Exclude<StableBusinessModeCode, 'repair'> | undefined {
  return isRentalBusinessMode(code) || code === 'buyout' ? code : undefined;
}

const DEFAULT_OR_RENTAL_HIDDEN_FIELDS = new Set([
  'device_unit_price',
  'unit_price_subtotal',
  'tax_inclusive_plan_price',
  'purchase_final_price_tax13',
  'purchase_final_price_ex_tax13',
  'purchase_final_price_untaxed',
  'device_untaxed_plan_price',
  'untaxed_plan_price',
  'device_compensation_price',
  'product_final_discount_price',
]);

const SHARED_WORKBENCH_PREFIX_FIELDS = new Set([
  'device_image',
  'device_name',
  'quantity',
  'warehouse_stock',
  'device_attribute',
]);

const PURCHASE_VISIBLE_FIELDS = new Set([
  ...SHARED_WORKBENCH_PREFIX_FIELDS,
  'retail_price_tax13',
  'item_discount',
  'purchase_final_price_tax13',
  'replacement_device',
]);

/** Column membership is mode-aware only after the shared identity/stock/
 * device-attribute prefix. The underlying field selection stays intact, so
 * switching modes is reversible and never mutates the administrator pool. */
export function quoteWorkbenchFieldVisible(
  businessModeCode: BusinessModeCode,
  fieldKey: string,
): boolean {
  if (businessModeCode === 'buyout')
    return PURCHASE_VISIBLE_FIELDS.has(fieldKey);
  if (
    businessModeCode === 'full' ||
    isRentalBusinessMode(businessModeCode) ||
    businessModeCode === 'trade'
  ) {
    return !DEFAULT_OR_RENTAL_HIDDEN_FIELDS.has(fieldKey);
  }
  return true;
}

export function updateBusinessModeCatalog(
  modes: BusinessModeRecord[],
  code: StableBusinessModeCode,
  patch: Partial<Pick<BusinessModeRecord, 'label' | 'sortOrder' | 'active'>>,
): BusinessModeRecord[] {
  if ('code' in (patch as object))
    throw new Error('业务模式 code 发布后不可修改');
  if (patch.label !== undefined && patch.label.trim().length === 0) {
    throw new Error('业务模式 label 不能为空');
  }
  if (patch.sortOrder !== undefined && !Number.isFinite(patch.sortOrder)) {
    throw new Error('业务模式排序必须是有限数值');
  }
  if (!modes.some((mode) => mode.code === code)) {
    throw new Error(`业务模式稳定 code 不存在：${code}`);
  }
  const next = modes.map((mode) =>
    mode.code === code
      ? {
          ...mode,
          ...patch,
          label: patch.label?.trim() ?? mode.label,
          code: mode.code,
        }
      : { ...mode },
  );
  if (new Set(next.map((mode) => mode.code)).size !== next.length) {
    throw new Error('业务模式稳定 code 不可复用');
  }
  return next;
}

export function isStableBusinessModeCode(
  code: BusinessModeCode,
): code is StableBusinessModeCode {
  return DEFAULT_MODES.some((mode) => mode.code === code);
}

export function legacyBusinessModeLabel(code: LegacyBusinessModeCode): string {
  return LEGACY_LABELS[code];
}

export function businessModeLabel(
  modes: BusinessModeRecord[],
  code: BusinessModeCode,
): string {
  if (!isStableBusinessModeCode(code)) return legacyBusinessModeLabel(code);
  return modes.find((mode) => mode.code === code)?.label ?? code;
}

/** Draft display follows the catalog; quote snapshots are only compatibility
 * input and publication freeze material. */
export function currentBusinessModeLabel(
  modes: BusinessModeRecord[],
  code: BusinessModeCode,
  legacySnapshot: string,
): string {
  return isStableBusinessModeCode(code)
    ? (modes.find((mode) => mode.code === code)?.label ?? code)
    : legacySnapshot;
}

export function requireActiveBusinessMode(
  modes: BusinessModeRecord[],
  code: StableBusinessModeCode,
): BusinessModeRecord {
  const mode = modes.find((item) => item.code === code && item.active);
  if (!mode) throw new Error(`业务模式 ${code} 已停用或不存在，不能用于新草稿`);
  return mode;
}
